import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Code, 
  Globe, 
  Brain, 
  Wrench, 
  Cpu, 
  Award,
  Star,
  TrendingUp
} from 'lucide-react';

const Skills = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedProgress, setAnimatedProgress] = useState({});
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate progress bars after component becomes visible
          setTimeout(() => {
            animateProgressBars();
          }, 500);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const animateProgressBars = () => {
    const progressData = {
      'C++': 85,
      'Python': 90,
      'C': 80,
      'JavaScript': 80,
      'HTML/CSS': 85,
      'React': 75,
      'Machine Learning': 75,
      'Data Analytics': 75,
      'Generative AI': 80,
      'Git/GitHub': 85,
      'VS Code': 90,
      'Streamlit': 80,
      'Arduino': 80,
      'Raspberry Pi': 75,
      'Circuit Design': 70
    };

    Object.keys(progressData).forEach((skill, index) => {
      setTimeout(() => {
        setAnimatedProgress(prev => ({
          ...prev,
          [skill]: progressData[skill]
        }));
      }, index * 200);
    });
  };

  const skillCategories = [
    {
      title: "Programming Languages",
      icon: Code,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-100 dark:bg-blue-900/30",
      skills: [
        { name: "C++", level: 85, description: "Data structures & algorithms" },
        { name: "Python", level: 90, description: "AI/ML & backend development" },
        { name: "C", level: 80, description: "System programming" }
      ]
    },
    {
      title: "Web Development",
      icon: Globe,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/30",
      skills: [
        { name: "HTML/CSS", level: 85, description: "Responsive design & modern CSS" },
        { name: "JavaScript", level: 80, description: "ES6+ & DOM manipulation" },
        { name: "React", level: 75, description: "Component-based development" }
      ]
    },
    {
      title: "AI & Data Science",
      icon: Brain,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/30",
      skills: [
        { name: "Machine Learning", level: 75, description: "Supervised & unsupervised learning" },
        { name: "Data Analytics", level: 70, description: "Pandas & data visualization" },
        { name: "Generative AI", level: 80, description: "OpenAI API & prompt engineering" }
      ]
    },
    {
      title: "Tools & Technologies",
      icon: Wrench,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-100 dark:bg-orange-900/30",
      skills: [
        { name: "Git/GitHub", level: 85, description: "Version control & collaboration" },
        { name: "VS Code", level: 90, description: "Development environment" },
        { name: "Streamlit", level: 80, description: "Rapid web app development" }
      ]
    },
    {
      title: "Hardware & IoT",
      icon: Cpu,
      color: "text-red-600 dark:text-red-400",
      bgColor: "bg-red-100 dark:bg-red-900/30",
      skills: [
        { name: "Arduino", level: 80, description: "Microcontroller programming" },
        { name: "Raspberry Pi", level: 75, description: "IoT & embedded systems" },
        { name: "Circuit Design", level: 70, description: "Basic electronics" }
      ]
    }
  ];

  const certifications = [
    {
      title: "Generative AI Mastermind",
      issuer: "Outskill",
      year: "2025",
      icon: Brain,
      color: "text-purple-600"
    },
    {
      title: "Machine Learning & Image Processing",
      issuer: "Zikshaa",
      year: "2024",
      icon: TrendingUp,
      color: "text-blue-600"
    },
    {
      title: "Data Analytics with Pandas",
      issuer: "NIELIT",
      year: "2024",
      icon: Star,
      color: "text-green-600"
    }
  ];

  return (
    <section id="skills" ref={sectionRef} className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Skills & <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Expertise</span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            A comprehensive overview of my technical skills and professional competencies
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-16">
          {skillCategories.map((category, categoryIndex) => (
            <Card 
              key={category.title}
              className={`hover:shadow-lg transition-all duration-500 hover:scale-105 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${categoryIndex * 150}ms` }}
            >
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${category.bgColor}`}>
                    <category.icon className={`w-5 h-5 ${category.color}`} />
                  </div>
                  <span className="text-lg font-semibold text-slate-900 dark:text-white">
                    {category.title}
                  </span>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {category.skills.map((skill, skillIndex) => (
                  <div key={skill.name} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {skill.name}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {animatedProgress[skill.name] || 0}%
                      </span>
                    </div>
                    
                    <Progress 
                      value={animatedProgress[skill.name] || 0} 
                      className="h-2"
                      style={{
                        transition: 'all 1s ease-out',
                        transitionDelay: `${(categoryIndex * 3 + skillIndex) * 200}ms`
                      }}
                    />
                    
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {skill.description}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Certifications Section */}
        <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-8 text-center">
            Certifications & Achievements
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <Card 
                key={cert.title}
                className={`text-center hover:shadow-lg transition-all duration-300 hover:scale-105 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${800 + index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col items-center space-y-4">
                    <div className={`p-4 rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30`}>
                      <cert.icon className={`w-8 h-8 ${cert.color} dark:text-cyan-400`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-sm">
                        {cert.title}
                      </h4>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                          {cert.issuer}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {cert.year}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Additional Skills Tags */}
        <div className={`mt-12 text-center transition-all duration-1000 delay-900 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
            Additional Competencies
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Problem Solving', 'Algorithm Design', 'System Design', 'Database Management',
              'API Development', 'Testing & Debugging', 'Project Management', 'Team Collaboration',
              'Technical Writing', 'Code Review', 'Agile Methodology', 'Open Source'
            ].map((skill, index) => (
              <Badge 
                key={skill}
                variant="secondary"
                className={`px-4 py-2 text-sm font-medium bg-gradient-to-r from-slate-100 to-blue-100 dark:from-slate-800 dark:to-blue-900/30 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:scale-110 transition-all duration-200`}
                style={{ animationDelay: `${1000 + index * 50}ms` }}
              >
                {skill}
              </Badge>
            ))}
          </div>
        </div>

        {/* Learning Philosophy */}
        <div className={`mt-16 text-center transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 p-8 rounded-xl border border-blue-100 dark:border-blue-800">
            <Award className="w-12 h-12 text-blue-600 dark:text-cyan-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
              Continuous Learning Mindset
            </h3>
            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Technology evolves rapidly, and so do I. I'm committed to staying updated with the latest 
              trends, tools, and best practices in software development and emerging technologies. 
              Every project is an opportunity to learn something new.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;